import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { reset } from "./reset";
import { AUTH_DELETE, AUTH_GET, AUTH_POST, AUTH_PUT } from "@grudder/app/actions-client";
import { config } from "@grudder/lib/config";
import { RootState } from "../store";

interface UserSettingState {
  userSettings: any[];
  loading: boolean;
  error: string | null;
  isOrderChanged: boolean;
  columnOrder: string[];
  savingCustomReport: boolean;
  lastSavedCustomReportId?: string | null;
}

// Define the initial state
const initialState: UserSettingState = {
  userSettings: [],
  loading: false,
  error: null,
  isOrderChanged: false,
  columnOrder: [],
  savingCustomReport: false,
  lastSavedCustomReportId: null,
};

/**
 * Validates settings object structure before sending to API
 * @param settings Settings to validate
 * @returns True if valid, false otherwise
 */
const validateSettings = (settings: any): boolean => {
  // Add validation logic here based on your API requirements
  // Example: Check if required fields exist
  if (!settings) return false;

  // For TABLE_CONFIG type, ensure config array exists and is properly structured
  if (settings.attributes && settings.attributes.config) {
    if (!Array.isArray(settings.attributes.config)) {
      return false;
    }

    // Add more specific validation as needed
  }

  return true;
};

// Fetch user settings async thunk
export const fetchUserSettings = createAsyncThunk("userSettings/fetchUserSettings", async (_, { rejectWithValue }) => {
  try {
    // console.log("Fetching user settings from API");
    const response = await AUTH_GET(`${config.baseURL}/api/users/user-settings`);
    //console.log("User settings fetched:", response);
    return response;
  } catch (error: any) {
    console.error("Error fetching user settings:", error);
    return rejectWithValue(error);
  }
});

// Update user settings async thunk
export const updateUserSettings = createAsyncThunk("userSettings/updateUserSettings", async (payload: { settingTypeId: string; settings: any }, { rejectWithValue }) => {
  try {
    // Ensure settings.config is an array, not an object with numeric keys
    const settingsToSend = { ...payload.settings };

    // If we have a config property but it's not an array, convert it
    if (settingsToSend.config && !Array.isArray(settingsToSend.config)) {
      settingsToSend.config = Object.values(settingsToSend.config);
    }

    // Validate the settings
    if (!validateSettings(settingsToSend)) {
      return rejectWithValue({
        message: "Invalid settings format. Please check your settings structure.",
        errorCode: 400,
      });
    }

    console.log("Sending API request with payload:", settingsToSend);

    const response = await AUTH_PUT(`${config.baseURL}/api/users/user-settings/${payload.settingTypeId}`, settingsToSend);

    console.log("Settings updated successfully:", response);
    return response;
  } catch (error: any) {
    console.error("Error updating user settings:", error);
    // Improve error handling by extracting more information if available
    const errorMessage = error?.message || "Failed to update user settings";
    return rejectWithValue({ message: errorMessage, errorCode: error?.errorCode || 500 });
  }
});

export const removeUserSettings = createAsyncThunk("userSettings/removeUserSettings", async (payload: { settingTypeId: string }, { rejectWithValue, getState, dispatch }) => {
  try {
    await AUTH_DELETE(`${config.baseURL}/api/users/user-settings/${payload.settingTypeId}`);
    const state = getState() as RootState;
    const filteredUserSettings = state.userSettings.userSettings.filter((setting: any) => setting._id !== payload.settingTypeId);
    dispatch(setUserSettings(filteredUserSettings));
    return null;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to remove user settings');
  }
});


export const resetUserSettings = createAsyncThunk(
  "userSettings/resetUserSettings",
  async (payload: { settingTypeId: string; settings: any }, { rejectWithValue }) => {
    try {
      const response = await AUTH_PUT(
        `${config.baseURL}/api/users/user-settings/${payload.settingTypeId}`,
        payload.settings
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to update settings');
    }
  }
)

export const createUserSettings = createAsyncThunk("userSettings/createUserSettings", async (payload: { settings: any }, { rejectWithValue }) => {
  try {
    // Ensure settings.config is an array, not an object with numeric keys
    const settingsToSend = { ...payload.settings };
    // If we have a config property but it's not an array, convert it
    if (settingsToSend.config && !Array.isArray(settingsToSend.config)) {
      settingsToSend.config = Object.values(settingsToSend.config);
    }
    // Validate the settings
    if (!validateSettings(settingsToSend)) {
      return rejectWithValue({
        message: "Invalid settings format. Please check your settings structure.",
        errorCode: 400,
      });
    }
    console.log("Sending API request with payload:", settingsToSend);
    const response = await AUTH_POST(`${config.baseURL}/api/users/user-settings`, settingsToSend);
    console.log("Settings created successfully:", response);
    return response;
  } catch (error: any) {
    console.error("Error creating user settings:", error);
    // Improve error handling by extracting more information if available
    const errorMessage = error?.message || "Failed to create user settings";
    return rejectWithValue({ message: errorMessage, errorCode: error?.errorCode || 500 });
  }
});

// Save a custom report (encapsulated in user settings API)
export const saveCustomReportSetting = createAsyncThunk(
  'userSettings/saveCustomReport',
  async (payload: { report: any }, { rejectWithValue }) => {
    try {
      const settingsBody = {
        settingsType: 'CUSTOM_REPORT',
        value: payload.report?.name || payload.report?.title || 'Custom Report',
        attributes: {
          data: payload.report
        }
      };
      const response = await AUTH_POST(`${config.baseURL}/api/users/user-settings`, settingsBody);
      return response; // Expect response to contain at least id/_id
    } catch (error: any) {
      console.error('Error saving custom report setting:', error);
      return rejectWithValue(error?.message || 'Failed to save custom report');
    }
  }
);

// Define the slice
const userSettingSlice = createSlice({
  name: "userSettings", // Changed from userSetting to userSettings for consistency
  initialState,
  reducers: {
    setUserSettings: (state, action: PayloadAction<any[]>) => {
      state.userSettings = action.payload;
    },
    setOrderChanged: (state, action: PayloadAction<boolean>) => {
      state.isOrderChanged = action.payload;
    },
    resetOrderChanged: (state) => {
      state.isOrderChanged = false;
    },
    // New action to update column order
    setColumnOrder: (state, action: PayloadAction<string[]>) => {
      state.columnOrder = action.payload;
      state.isOrderChanged = true; // Mark that order has changed
    },
    // Update the fields in the user settings based on the column order
    updateFieldsOrder: (state, action: PayloadAction<string[]>) => {
      const newOrder = action.payload;
      const tableConfigSetting = state.userSettings.find((setting) => setting?.settingsType === "TABLE_CONFIG" && setting?.value === "lead");

      if (tableConfigSetting && tableConfigSetting.attributes?.config) {
        // Create a map for fast lookups by accessorKey
        const configMap = new Map(tableConfigSetting.attributes.config.map((item: any) => [item.accessorKey, item]));

        // Create a new config array with the items ordered according to newOrder
        // but only if the keys exist in the current config
        const newConfig = newOrder.filter((key) => configMap.has(key)).map((key) => configMap.get(key));

        // Add any items that weren't in the newOrder to preserve all settings
        const includedKeys = new Set(newOrder);
        const remainingItems = tableConfigSetting.attributes.config.filter((item: any) => !includedKeys.has(item.accessorKey));

        // Update the config with the new order
        tableConfigSetting.attributes.config = [...newConfig, ...remainingItems];
        state.isOrderChanged = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchUserSettings actions
      .addCase(fetchUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.userSettings = action.payload?.userSettings || [];
        // Initialize columnOrder from fetched settings
        const tableConfigSetting = action.payload?.userSettings?.find((setting: any) => setting?.settingsType === "TABLE_CONFIG" && setting?.value === "lead");

        if (tableConfigSetting?.attributes?.config) {
          // Fixed columns always come first
          const fixedColumns = ["select", "fullName"];

          // Extract accessorKeys from config
          const configKeys = tableConfigSetting.attributes.config.filter((item: any) => item.show && !fixedColumns.includes(item.accessorKey)).map((item: any) => item.accessorKey);

          state.columnOrder = [...fixedColumns, ...configKeys];
        }
      })
      .addCase(fetchUserSettings.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch user settings";
      })
      // Handle updateUserSettings actions
      .addCase(updateUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        const responseCollection = action.payload?.userSettings;

        if (Array.isArray(responseCollection)) {
          state.userSettings = responseCollection;

          const tableConfigSetting = responseCollection.find((setting: any) => setting?.settingsType === "TABLE_CONFIG" && setting?.value === "lead");

          if (tableConfigSetting?.attributes?.config) {
            const fixedColumns = ["select", "fullName"];
            const configKeys = tableConfigSetting.attributes.config
              .filter((item: any) => item.show && !fixedColumns.includes(item.accessorKey))
              .map((item: any) => item.accessorKey);
            state.columnOrder = [...fixedColumns, ...configKeys];
          }
        } else {
          const responseSetting = action.payload?.userSetting ?? (Array.isArray(action.payload) ? action.payload[0] : action.payload);
          const incomingSetting = responseSetting ?? action.meta?.arg?.settings;
          if (incomingSetting) {
            const existingIndex = state.userSettings.findIndex((setting: any) => setting?._id === action.meta?.arg?.settingTypeId);
            const existing = existingIndex >= 0 ? state.userSettings[existingIndex] : undefined;
            const mergedSetting = {
              ...(existing ?? {}),
              ...(incomingSetting ?? {}),
              _id: action.meta?.arg?.settingTypeId ?? existing?._id,
            };
            const existingAttributes = (existing?.attributes ?? {}) as Record<string, unknown>;
            const incomingAttributes = (incomingSetting?.attributes ?? {}) as Record<string, unknown>;
            if (Object.keys(existingAttributes).length > 0 || Object.keys(incomingAttributes).length > 0) {
              mergedSetting.attributes = { ...existingAttributes, ...incomingAttributes };
            }

            if (existingIndex >= 0) {
              state.userSettings[existingIndex] = mergedSetting;
            } else {
              state.userSettings.push(mergedSetting);
            }
          }
        }
        state.isOrderChanged = false;
        // Reset the flag after successful update
      })
      .addCase(updateUserSettings.rejected, (state, action: any) => {
        state.loading = false;
        // Improve error handling by providing more detailed error messages
        state.error = action.payload?.message || "Failed to update user settings";
        console.error("Settings update rejected:", action.payload);
      })
      .addCase(createUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        const responseCollection = action.payload?.userSettings;
        if (Array.isArray(responseCollection)) {
          state.userSettings = responseCollection;
          return;
        }

        const responseSetting = action.payload?.userSetting ?? (Array.isArray(action.payload) ? action.payload[0] : action.payload);
        const incomingSetting = responseSetting ?? action.meta?.arg?.settings;
        if (!incomingSetting) return;

        const clonedSetting = JSON.parse(JSON.stringify(incomingSetting));
        const createdId = action.payload?._id ?? action.payload?.id ?? action.payload?.settingTypeId;
        if (createdId && !clonedSetting._id) {
          clonedSetting._id = createdId;
        }
        state.userSettings.push(clonedSetting);
      })
      .addCase(createUserSettings.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create user settings";
      })
      // Save custom report cases
      .addCase(saveCustomReportSetting.pending, (state) => {
        state.savingCustomReport = true;
        state.error = null;
      })
      .addCase(saveCustomReportSetting.fulfilled, (state, action) => {
        state.savingCustomReport = false;
        const saved = action.payload;
        state.lastSavedCustomReportId = saved?.id || saved?._id || null;
        // Optionally push into userSettings list
        if (saved) {
          state.userSettings.push(saved);
        }
      })
      .addCase(saveCustomReportSetting.rejected, (state, action: any) => {
        state.savingCustomReport = false;
        state.error = action.payload || 'Failed to save custom report';
      })
      .addCase(reset, () => initialState); // Handle reset action
  },
});

export const { setUserSettings, setOrderChanged, resetOrderChanged, setColumnOrder, updateFieldsOrder } = userSettingSlice.actions;

export default userSettingSlice.reducer;
